import type { FragmentType } from '@/generated/gql/fragment-masking'
import { useFragment } from '@/generated/gql/fragment-masking'
import { graphql } from '@/generated/gql/gql'
import { datadogRum } from '@datadog/browser-rum'
import { Drawer } from '@mui/material'

import { useState } from 'react'

import { Cart } from './Cart/index'
import TopUpSection from './TopUp'
import type { CanteenCartItem } from './types'

const canteenMenuCartMenuDataFragment = graphql(`
  fragment CanteenMenuCartV2_MenuDataFragment on CCMealService {
    availableSubsidy
    timeslots {
      id
      startTime
      endTime
    }
  }
`)

const canteenMenuCartUserDataFragment = graphql(`
  fragment CanteenMenuCartV2_UserDataFragment on User {
    ...CanteenCart_UserDataFragment
    ...CanteenCartTopUpV2_UserDataFragment
    subsidySettings {
      unlimitedSubsidy
    }
    wallet {
      balance
    }
  }
`)

export enum CartSection {
  Order = 1,
  TopUp = 2,
}

interface MenuCartV2Props {
  open: boolean
  arrivalTime: string
  onClose: () => void
  onChangeTimeslot: (timeslot: string) => void
  onUpdate: (updatedItem: CanteenCartItem) => void
  onRemove: (itemReference: string) => void
  placeOrder: (bundle: number) => Promise<void>
  placingOrder: boolean
  cartItems: CanteenCartItem[]
  cartTotal: number
  currency: string
  userData: FragmentType<typeof canteenMenuCartUserDataFragment>
  menuData: FragmentType<typeof canteenMenuCartMenuDataFragment>
  orderTimeRemaining: number
}

export function MenuCartV2(props: MenuCartV2Props) {
  const {
    currency,
    placeOrder,
    arrivalTime,
    cartItems,
    menuData: menuDataProp,
    userData: userDataProp,
    onClose,
    open,
    onUpdate,
    onChangeTimeslot,
    onRemove,
    cartTotal,
    placingOrder,
    orderTimeRemaining,
  } = props
  const menuData = useFragment(canteenMenuCartMenuDataFragment, menuDataProp)
  const userData = useFragment(canteenMenuCartUserDataFragment, userDataProp)

  const { availableSubsidy = 0, timeslots = [] } = menuData

  const [currentSection, setCurrentSection] = useState<CartSection>(CartSection.Order)
  const [error, setError] = useState<string | null>(null)
  const [orderSucceeded] = useState(false)

  const handlePlaceOrder = (bundle: number) => {
    placeOrder(bundle)
  }

  const onCheckoutClicked = () => {
    const personalCredits = parseFloat(userData.wallet.balance.toString())
    const subtotal = cartTotal

    datadogRum.addAction('Checkout', {
      items: cartItems,
      subtotal,
    })

    if (
      !userData.subsidySettings.unlimitedSubsidy &&
      subtotal - availableSubsidy - personalCredits > 0
    ) {
      setCurrentSection(CartSection.TopUp)
    } else {
      handlePlaceOrder(0)
    }
  }

  const onTopUpCheckoutClicked = (bundle: number) => {
    handlePlaceOrder(bundle)
  }

  const onBackClicked = () => {
    setCurrentSection(CartSection.Order)
  }

  const onDismissError = () => {
    setError(null)
  }

  let content
  switch (currentSection) {
    case CartSection.TopUp:
      content = (
        <TopUpSection
          currency={currency}
          availableSubsidy={availableSubsidy}
          userData={userData}
          cartTotal={cartTotal}
          onBackClicked={onBackClicked}
          onCheckoutClicked={(bundle) => {
            onTopUpCheckoutClicked(bundle)
          }}
          loading={placingOrder}
        />
      )
      break
    default:
      content = (
        <Cart
          currency={currency}
          cartTotal={cartTotal}
          onRemove={onRemove}
          userData={userData}
          availableSubsidy={availableSubsidy}
          timeslots={timeslots}
          onChangeTimeslot={onChangeTimeslot}
          arrivalTime={arrivalTime}
          cartItems={cartItems}
          onClose={onClose}
          onUpdate={onUpdate}
          onCheckoutClicked={() => {
            onCheckoutClicked()
          }}
          onDismissError={onDismissError}
          loading={placingOrder}
          orderSucceeded={orderSucceeded}
          error={error}
          orderTimeRemaining={orderTimeRemaining}
        />
      )
      break
  }

  return (
    <Drawer
      sx={{
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width: { sm: 430, xs: '100%' },
        },
      }}
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}>
      {content}
    </Drawer>
  )
}
